import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, ConfirmDialog, DataGrid, useToast, type DataGridColumn } from '@kss/ui';
import { Plus, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { Attribute } from '@kss/api';
import { attributeService } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';

export default function AllAttributes() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const perms = useModulePerms('Attribute');
  const [rows, setRows] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Attribute | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await attributeService.all(); setRows(Array.isArray(data) ? data : []); }
    catch (e) { const msg = e instanceof Error ? e.message : 'Failed to load'; setError(msg); toastRef.current.error('Load failed', msg); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.attributeId) return;
    setDeleting(true);
    try { await attributeService.delete(confirmDelete.attributeId); toast.success('Deleted', `${confirmDelete.name} removed`); setConfirmDelete(null); await load(); }
    catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const columns: DataGridColumn<Attribute>[] = [
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    { accessor: 'description', title: 'Description', sortable: true, render: (r) => r.description || '—' },
    { accessor: 'attributeId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover" onClick={(e) => { e.stopPropagation(); if (r.attributeId) navigate(`/panel/attributes/${encodeId(r.attributeId)}`); }} title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
          )}
          {perms.canDelete && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover" onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
          )}
        </div>
      ) },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<Attribute>
        title={<span className="hidden sm:inline">All attributes</span>}
        columnPicker data={rows} columns={columns} defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
            <StandardAddDropdown basePath="/panel/attributes" newLabel="New attribute" service={attributeService} templateFields={['name', 'code', 'description', 'isActive']} module="Attribute" />
          </div>
        }
      />
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))} onConfirm={handleDelete}
        title="Delete attribute" message={confirmDelete ? `Remove "${confirmDelete.name}"?` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}
